import dayjs from 'dayjs';

// Async by design: it fetches existing names via a getCampaigns callback so it can
// guarantee the new name is unique across the server-side list.

const formatCurrentDate = (): string => dayjs(new Date()).format('YYYY-MM-DD');

const getDatedBaseName = (originalName: string): string => {
  const datePrefixRegex = /^\d{4}-\d{2}-\d{2} - /;
  let baseName = originalName.replace(datePrefixRegex, '');

  const counterSuffixRegex = /\s*\(\d+\)$/;
  baseName = baseName.replace(counterSuffixRegex, '');

  return `${formatCurrentDate()} - ${baseName}`;
};

/** Matches the old builder's safe name when the uniqueness lookup is unavailable. */
export const generateCampaignCopyFallbackName = (originalName: string): string =>
  `${getDatedBaseName(originalName)} (Copy)`;

/**
 * Given a campaign's current name, generate a unique name by:
 *  1. Stripping any leading date prefix (YYYY-MM-DD - ) and trailing counter suffix ( (N)).
 *  2. Re-prepending today's date.
 *  3. If that name is already taken, appending an incrementing counter.
 *
 * @param originalName  The source campaign's name.
 * @param companyId     The company to look up existing campaigns for.
 * @param getCampaigns  Callback that returns `{ items: { name: string }[]; count: number }`.
 */
export async function generateUniqueCampaignName(
  originalName: string,
  companyId: string,
  getCampaigns: (companyId: string) => Promise<{ items: { name: string }[]; count: number }>,
): Promise<string> {
  const newNameWithDate = getDatedBaseName(originalName);

  // Rejects if the lookup fails. A name that was never checked against the
  // existing ones is not unique, and saving under it means the API answers
  // "already exists" about a name the user never typed.
  const existingNames = (await getCampaigns(companyId)).items.map(c => c.name);

  if (!existingNames.includes(newNameWithDate)) return newNameWithDate;

  let counter = 1;
  while (existingNames.includes(`${newNameWithDate} (${counter})`)) counter++;
  return `${newNameWithDate} (${counter})`;
}
